import React, { useEffect, useState } from 'react'
import './qrCodeDisplay.css'

export interface QrCodeDisplayProps {
  value: string
  size?: number
  ariaLabel?: string
  className?: string
  testId?: string
}

export const QrCodeDisplay: React.FC<QrCodeDisplayProps> = ({
  value,
  size = 200,
  ariaLabel = 'QR Code',
  className = '',
  testId = 'qr-code-display',
}) => {
  const [svgTag, setSvgTag] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true
    if (!value) {
      queueMicrotask(() => {
        if (isMounted) {
          setSvgTag('')
        }
      })
      return
    }

    import('qrcode-generator')
      .then((mod) => {
        if (!isMounted) return
        const qrcodeFactory =
          (mod as { default?: typeof qrcode }).default || (mod as unknown as typeof qrcode)
        try {
          const qr = qrcodeFactory(0, 'M')
          qr.addData(value)
          qr.make()
          const generatedSvg = qr.createSvgTag({ scalable: true })
          if (isMounted) {
            setSvgTag(generatedSvg)
            setError(null)
          }
        } catch (err) {
          if (isMounted) {
            setError(err instanceof Error ? err.message : 'QR code generation failed')
          }
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load QR generator')
        }
      })

    return () => {
      isMounted = false
    }
  }, [value])

  return (
    <div
      className={`qrCodeDisplay ${className}`}
      data-testid={testId}
      aria-label={ariaLabel}
      role="img"
    >
      {error ? (
        <div className="qrCodeDisplay__error" role="alert">
          {error}
        </div>
      ) : svgTag ? (
        <div
          style={{ width: size, height: size }}
          className="qrCodeDisplay__svg"
          dangerouslySetInnerHTML={{ __html: svgTag }}
        />
      ) : (
        <div
          style={{ width: size, height: size }}
          className="qrCodeDisplay__svg"
        >
          …
        </div>
      )}
    </div>
  )
}
